/**
 * THE ROOT FATE AND THE PUBLIC ENTRY RULE (launch-marketing-pages M1).
 *
 * `/` is Home, a public page; the workspace lives at `WORKSPACE_PATH`. Links
 * written before the move still point at `/` (a member's bookmark, an
 * installed app, a `/?state=` or `/?focusType=` link in an old email), so the
 * Home record decides, on the way in, whether the visitor meant the workspace.
 *
 * `rootRouteFate` is the `Home` child's `beforeEnter`, never `PublicLayout`'s:
 * a member opening a shared `/owners` link sees the Owner page. It acts on an
 * ENTRY navigation only (`from` is `START_LOCATION`, which holds across the
 * redirects inside the first navigation), and forwards to the workspace,
 * query and hash kept, when either:
 *
 *   1. a session token is held. It is read synchronously, never waiting on
 *      whoami: a dead token forwards too, and `handleSessionExpired` sends the
 *      visitor on to `/sign-in`, as a dead token on `/` always did;
 *   2. any query key fails the public entry rule.
 *
 * Otherwise Home renders, and an in-app navigation to Home renders it for
 * anyone.
 *
 * ── THE RULE LISTS WHAT A PUBLIC ENTRY MAY CARRY ──────────────────────────
 * Never what the workspace reads: the workspace's flat params have no registry
 * and would drift. So any key the rule does not name forwards, and forwarding
 * a guest's `?horizontalTab=Authenticate` is right (`MainView` sends that
 * guest on to `/sign-in`). The rule is the campaign and click-id vocabulary of
 * the channels that link to a marketing page; `rootRoute.spec.js` holds it
 * against every workspace key, importing the real constants.
 *
 * A leaf: it imports `vue-router`'s `START_LOCATION` and the workspace path,
 * nothing else from the app, and reads no store.
 */
import { START_LOCATION } from "vue-router";
import { WORKSPACE_PATH } from "@/constants/workspacePath";

/** THE ONE PUBLIC ENTRY RULE: a key passes by prefix, by suffix, or by name. */
export const PUBLIC_ENTRY_PARAMS = Object.freeze({
  prefixes: Object.freeze(["utm_"]),
  // `gclid`, `fbclid`, `twclid`, `ttclid`, `dclid`, `yclid`, ...
  suffixes: Object.freeze(["clid"]),
  names: Object.freeze([
    "msclkid",
    "_gl",
    "gbraid",
    "wbraid",
    "srsltid",
    "igsh",
    "igshid",
    "li_fat_id",
    "_hsenc",
    "_hsmi",
    "mc_cid",
    "mc_eid",
    "mkt_tok",
    "rdt_cid",
    "epik",
    "ref",
  ]),
});

/**
 * May a public entry carry this query key and still show the public page?
 *
 * @param {string} key
 * @returns {boolean}
 */
export function isPublicEntryParam(key) {
  if (typeof key !== "string") return false;
  return (
    PUBLIC_ENTRY_PARAMS.names.includes(key) ||
    PUBLIC_ENTRY_PARAMS.prefixes.some((prefix) => key.startsWith(prefix)) ||
    PUBLIC_ENTRY_PARAMS.suffixes.some((suffix) => key.endsWith(suffix))
  );
}

/**
 * THE ROOT FATE: Home, or the workspace with the address's query and hash.
 *
 * @param {import("vue-router").RouteLocationNormalized} to
 * @param {import("vue-router").RouteLocationNormalized} from
 * @param {boolean} hasToken whether a session token is held
 * @returns {true|{ path: string, query: object, hash: string }}
 */
export function rootRouteFate(to, from, hasToken) {
  if (from !== START_LOCATION) return true;

  const query = to.query ?? {};
  if (hasToken || Object.keys(query).some((key) => !isPublicEntryParam(key))) {
    return { path: WORKSPACE_PATH, query, hash: to.hash ?? "" };
  }

  return true;
}
